import { Input, Select, Tag, Tooltip } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import { TableColumnInterface } from '../../../../shared/type/tableType';
import { formatDateTimePST } from '../../../../shared/utils/timeFormat';
import { mediaholdIngestService } from '../service/mediaholdIngestService';
import {
  MediaholdIngestHistoryItem,
  PROCESSED_STATUS_COLORS,
  ProcessedStatus,
  PROVIDER_TYPE_COLORS,
} from '../type/mediaholdIngestType';

const MediaholdIngestList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    page_size: '20',
    ordering: '-created',
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [_selectedList, setSelectedList] = useState<number[]>([]);

  // Filter states
  const [processed, setProcessed] = useState<string>(searchParams.get('processed') || '');
  const [providerType, setProviderType] = useState<string>(
    searchParams.get('provider_type') || '',
  );
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');

  useEffect(() => {
    // Update searchParams when filters change
    const newParams = new URLSearchParams(searchParams);

    if (processed) {
      newParams.set('processed', processed);
    } else {
      newParams.delete('processed');
    }

    if (providerType) {
      newParams.set('provider_type', providerType);
    } else {
      newParams.delete('provider_type');
    }

    if (searchQuery) {
      newParams.set('search', searchQuery);
    } else {
      newParams.delete('search');
    }

    newParams.set('page', '1'); // Reset to first page when filters change
    setSearchParams(newParams);
  }, [processed, providerType, searchQuery]);

  const columns: TableColumnInterface<MediaholdIngestHistoryItem>[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '5%',
      sorter: true,
      render: (_text: string, record: MediaholdIngestHistoryItem): JSX.Element => (
        <span style={{ color: '#1890ff', fontWeight: 500 }}>{record.id}</span>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider_type',
      key: 'provider_type',
      width: '7%',
      render: (_: string, record: MediaholdIngestHistoryItem): JSX.Element => (
        <Tag color={PROVIDER_TYPE_COLORS[record.provider_type] || 'default'}>
          {record.provider_type.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Program Name',
      dataIndex: 'program_name',
      key: 'program_name',
      width: '15%',
      render: (_text: string, record: MediaholdIngestHistoryItem): string => record.program_name,
    },
    {
      title: 'Episode No',
      dataIndex: 'episode_no',
      key: 'episode_no',
      width: '8%',
    },
    {
      title: 'On Air Date',
      dataIndex: 'on_air_date',
      key: 'on_air_date',
      width: '8%',
      sorter: true,
      render: (_text: string, record: MediaholdIngestHistoryItem): string => {
        if (!record.on_air_date) return '-';
        return record.on_air_date;
      },
    },
    {
      title: 'Network',
      dataIndex: 'network',
      key: 'network',
      width: '10%',
    },
    {
      title: 'Season',
      dataIndex: 'season_title',
      key: 'season',
      width: '12%',
      render: (_: string, record: MediaholdIngestHistoryItem): JSX.Element => {
        if (!record.season_id) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        return (
          <div>
            <Link
              to={`/season/edit2/${record.season_id}`}
              style={{ fontSize: '12px', fontWeight: 500, color: '#1890ff' }}
              onClick={e => e.stopPropagation()}
            >
              {record.season_id}
            </Link>
            {record.season_title && (
              <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
                {record.season_title.title.substring(0, 30)}
                {record.season_title.title.length > 30 ? '...' : ''}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Content',
      dataIndex: 'content_id',
      key: 'content_id',
      width: '8%',
      render: (_: string, record: MediaholdIngestHistoryItem): JSX.Element => {
        if (!record.content_id) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        return (
          <Link
            to={`/episode/edit/${record.content_id}`}
            style={{ fontSize: '12px', fontWeight: 500, color: '#1890ff' }}
            onClick={e => e.stopPropagation()}
          >
            {record.content_id}
          </Link>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'processed',
      key: 'processed',
      width: '8%',
      render: (_: string, record: MediaholdIngestHistoryItem): JSX.Element => (
        <div>
          <Tag color={PROCESSED_STATUS_COLORS[record.processed] || 'default'}>
            {record.processed.toUpperCase()}
          </Tag>
          {record.fail_count > 0 && (
            <div style={{ fontSize: '11px', color: '#ff4d4f', marginTop: 2 }}>
              Fail: {record.fail_count}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: '10%',
      sorter: true,
      render: (_text: string, record: MediaholdIngestHistoryItem): string =>
        formatDateTimePST(record.created),
    },
  ];

  const fetchData = useCallback(
    async (
      params: URLSearchParams,
    ): Promise<{ data: MediaholdIngestHistoryItem[]; count: number }> => {
      try {
        const result = await mediaholdIngestService.fetch(params);
        return { data: result.results, count: result.count };
      } catch (error) {
        console.error('Failed to fetch Mediahold ingest history:', error);
        return { data: [], count: 0 };
      }
    },
    [],
  );

  useEffect(() => {
    setRefreshCounter(prev => prev + 1);
  }, [searchParams]);

  return (
    <Component.BasePageComponent>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Tooltip
          title={
            <div>
              Searchable fields:
              <br />• Program Name
              <br />• Episode No
            </div>
          }
        >
          <Input.Search
            placeholder="Search Program Name, Episode No"
            allowClear
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onSearch={value => setSearchQuery(value)}
            style={{ width: 300 }}
            enterButton
          />
        </Tooltip>

        <Select
          style={{ width: 200 }}
          placeholder="Status: All"
          allowClear
          value={processed || undefined}
          onChange={value => setProcessed(value || '')}
        >
          <Select.Option value={ProcessedStatus.PENDING}>Pending</Select.Option>
          <Select.Option value={ProcessedStatus.COMPLETED}>Completed</Select.Option>
          <Select.Option value={ProcessedStatus.FAILED}>Failed</Select.Option>
        </Select>

        <Select
          style={{ width: 200 }}
          placeholder="Provider: All"
          allowClear
          value={providerType || undefined}
          onChange={value => setProviderType(value || '')}
        >
          <Select.Option value="mediahold">Mediahold</Select.Option>
          <Select.Option value="kcp">KCP</Select.Option>
        </Select>
      </div>

      <Component.TableComponent<MediaholdIngestHistoryItem>
        key={refreshCounter}
        name="Mediahold Ingest History"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dataFetch={fetchData}
        columns={columns}
        options={{
          isDisableIdColumn: true,
        }}
        actions={{
          onSelectionChange: setSelectedList,
          onRowClick: (record: MediaholdIngestHistoryItem) => {
            navigate(`/mediahold-ingest/detail/${record.id}`);
          },
        }}
      />
    </Component.BasePageComponent>
  );
};

export default observer(MediaholdIngestList);

